// @ts-nocheck
import { useEffect, useState } from "react";
import {
  CFormInput,
  CFormSelect,
  CSpinner,
  CFormTextarea,
} from "@coreui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BoxWrapper from "../../../components/BoxWrapper";
import { useCookie } from "../../../hooks/useCookie";
import { general } from "../../../locales/general";
import { useToast } from "../../../hooks/useToast";
import { useMutation, useQueryClient } from "react-query";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormMode } from "../../../helpers/enums";
import { useForm, Controller } from "react-hook-form";
import apiService from "../../../service/apiService";
import { verifyFileList } from "../../../helpers/general";
import FileUploader from "../../../components/FileUploader";
import InfiniteScrollSelect from "../../../components/InfiniteScrollSelect";

// 🔹 Type for form data
interface RewardTranslationFormData {
  title: string;
  description: string;
  picture?: File;
  lang: string;
  rewardId: { id: string; title: string };
}

export default function NewRewardTranslation({ mode = FormMode.CREATE }) {
  const location = useLocation();
  const { reward } = location.state || {}; // 👈 Pre-selected reward from parent
  const [formLoading, setFormLoading] = useState(false);
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [description, setDescription] = useState("");
  const [existingImage, setExistingImage] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedReward, setSelectedReward] = useState(reward || null);

  const schema = yup.object({
    title: yup.string().required(general.fr.validation.required),
    description: yup.string().nullable(),
    picture: yup.mixed().nullable(),
    lang: yup.string().required(general.fr.validation.required),
    rewardId: yup.object().required(general.fr.validation.required),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control: formControl,
    setValue: setFormValue,
  } = useForm<RewardTranslationFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      lang: "en",
      rewardId: reward || null,
    },
  });

  useEffect(() => {
    if (mode === FormMode.UPDATE && params?.id) {
      setFormLoading(true);
      apiService
        .MakeGetRequest(`rewards-translations/${params.id}`, token)
        .then((resp) => {
          setDescription(resp?.description || "");
          setExistingImage(resp?.image);
          setSelectedReward(resp?.reward);

          // Populate form fields
          setFormValue("title", resp?.title);
          setFormValue("lang", resp?.lang);
          setFormValue("rewardId", resp?.reward);
        })
        .catch((err) => {
          console.error("Failed to load translation:", err);
          toastError(general.fr.message.operationFailed);
          navigate(-1);
        })
        .finally(() => setFormLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id, mode]);

  const createMutation = useMutation(
    (formData: FormData) =>
      apiService.MakePostRequest("rewards-translations", formData, token, true), // true = multipart
    {
      onSuccess: () => {
        toastSuccess(general.fr.message.rewardTranslationCreated);
        queryClient.invalidateQueries("getAllRewardTranslations");
        navigate(-1);
      },
      onError: () => {
        toastError(general.fr.message.operationFailed);
        setFormLoading(false);
      },
    },
  );

  const updateMutation = useMutation(
    (formData: FormData) =>
      apiService.MakePutRequest(
        `rewards-translations/${params.id}`,
        formData,
        token,
        true,
      ),
    {
      onSuccess: () => {
        toastSuccess(general.fr.message.rewardTranslationUpdated);
        queryClient.invalidateQueries("getAllRewardTranslations");
        navigate(-1);
      },
      onError: () => {
        toastError(general.fr.message.operationFailed);
        setFormLoading(false);
      },
    },
  );

  const onSubmit = async (data: RewardTranslationFormData) => {
    setFormLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("description", description || "");
      formData.append("lang", data.lang);
      formData.append("rewardId", data.rewardId?.id);

      const pictureFile = verifyFileList(data.picture);
      if (pictureFile) {
        formData.append("picture", pictureFile);
      }

      if (mode === FormMode.UPDATE) {
        await updateMutation.mutateAsync(formData);
      } else {
        await createMutation.mutateAsync(formData);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setFormLoading(false);
      toastError(general.fr.message.operationFailed);
    }
  };

  const handleRewardSelect = (selected: { id: string; title: string }) => {
    setSelectedReward(selected);
    setFormValue("rewardId", selected);
  };

  return (
    <div className="m-4">
      <BoxWrapper>
        <section className="p-4">
          <div className="border-bottom border-success">
            <h4>
              {mode === FormMode.UPDATE
                ? "Mise à jour de la traduction"
                : "Ajouter une traduction de récompense"}
            </h4>
          </div>

          <form className="row form mt-4" onSubmit={handleSubmit(onSubmit)}>
            {/* 🔹 Reward Selection */}
            <div className="col-md-6 my-3">
              <label htmlFor="rewardId" className="d-block mb-2">
                Bonus <span className="text-md text-red">*</span>
              </label>
              <Controller
                name="rewardId"
                control={formControl}
                render={({ field }) => (
                  <InfiniteScrollSelect
                    name="rewardId"
                    id="rewardId"
                    isClearable={false}
                    defaultValue={reward}
                    error={errors?.rewardId?.message}
                    getOptionLabel={(option: Reward) => option?.title}
                    getOptionValue={(option: Reward) => option?.id}
                    isOptionSelected={(option: any, selectedValue: any) =>
                      option?.id === selectedValue?.[0]?.id
                    }
                    onSelect={handleRewardSelect}
                    url={{ path: "rewards" }}
                    {...field}
                  />
                )}
              />
              {errors.rewardId?.message && (
                <div className="text-red-500" style={{ color: "red" }}>
                  {errors.rewardId.message}
                </div>
              )}
            </div>

            {/* 🔹 Language Selection */}
            <div className="col-md-6 my-3">
              <label htmlFor="lang" className="d-block">
                Langue <span className="text-sm text-red">*</span>
              </label>
              <CFormSelect
                name="lang"
                id="lang"
                {...register("lang")}
                disabled={mode === FormMode.UPDATE}
              >
                <option value="en">Anglais</option>
                <option value="ar">Arabe</option>
              </CFormSelect>
              {errors.lang?.message && (
                <div className="text-red-500" style={{ color: "red" }}>
                  {errors.lang.message}
                </div>
              )}
            </div>

            {/* 🔹 Title Input */}
            <div className="col-md-6 mt-4">
              <label htmlFor="title" className="d-block">
                Titre <span className="text-sm text-red">*</span>
              </label>
              <CFormInput
                type="text"
                className="custom-input"
                placeholder="Titre de la récompense"
                id="title"
                name="title"
                {...register("title")}
              />
              {errors.title?.message && (
                <div className="text-red-500" style={{ color: "red" }}>
                  {errors.title.message}
                </div>
              )}
            </div>

            {/* 🔹 Image Upload */}
            <div className="col-md-6 mt-4">
              <label htmlFor="picture" className="d-block mb-1">
                Image de la récompense
              </label>
              <FileUploader
                name="picture"
                id="picture"
                accept="image/png,image/jpeg,image/jpg"
                {...register("picture")}
                file={existingImage}
                required={false}
              />
              {errors.picture?.message && (
                <div className="text-red-500" style={{ color: "red" }}>
                  {errors.picture.message}
                </div>
              )}
            </div>

            {/* 🔹 Rich Text Description */}
            {/* <div className="col-12 mt-4">
              <label htmlFor="description">Description</label>
              <RichTextEditor
                onChange={handleDescriptionChange}
                name="description"
                defaultValue={description}
                placeholder="Décrivez les conditions de la récompense..."
              />
            </div> */}

            <div className="col-12 mt-4">
              <label htmlFor="description" className="d-block mb-2">
                Description
              </label>
              <CFormTextarea
                id="description"
                name="description"
                rows={4}
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="custom-input"
              />
            </div>

            {/* 🔹 Action Buttons */}
            <section className="d-flex justify-content-center buttons gap-4 mt-5">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn btn-danger red-shadow w-20 px-4 py-2 text-white"
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                className="btn btn-success green-shadow w-20 text-white"
                type="submit"
                disabled={
                  formLoading ||
                  createMutation.isLoading ||
                  updateMutation.isLoading
                }
              >
                {formLoading ? (
                  <div className="text-center">
                    <CSpinner size="sm" color="light" />
                  </div>
                ) : mode === FormMode.UPDATE ? (
                  "Mettre à jour"
                ) : (
                  "Créer"
                )}
              </button>
            </section>
          </form>
        </section>
      </BoxWrapper>
    </div>
  );
}
